'use strict';

var _ = require('lodash');
var d3 = require('d3');

/**
 * Single region Brush
 * @param d3Helper
 * @param brushExtentService
 * @returns {Function}
 * @constructor
 */
var Brush = function Brush(d3Helper, brushExtentService) {
  return function () {
    var scales, params;
    var backBrushGroup, frontBrushGroup;
    var domainRange, currentSelection, sizeRange;
    var dispatch = d3.dispatch('brushchange', 'brushstart', 'brushend');

    /**
     * Constructs a new brush with no default x- and y-scale, and an empty extent.
     * Attaches event listeners to it.
     */
    function getSvgBrush() {
      var svgBrush = d3.svg.brush();
      svgBrush.on('brush', brushHandler);
      svgBrush.on('brushstart', brushStartHandler);
      svgBrush.on('brushend', brushEndHandler);
      return svgBrush;
    }

    /**
     * Creates brush group DOM element
     * @param container - parent container
     * @param prefix - brush name's prefix
     * @param brushScale
     * @param brushHeight
     */
    function createBrushGroup(container, prefix, brushScale, brushHeight) {
      var brushGroup = d3Helper.createGroupElement(container, prefix + '-brush');
      var r = brushHeight > 100 ? 6 : 0;
      var padding = prefix === 'front' ? 0 : 3;
      brushGroup.call(svgBackBrush);
      brushGroup.selectAll('rect').attr({
        rx: r,
        ry: r,
        y: -padding,
        height: brushHeight + 2 * padding
      });
      if (currentSelection !== undefined) {
        brushGroup.selectAll('rect.extent').attr({
          x: brushScale(currentSelection[0]),
          width: brushScale(currentSelection[1]) - brushScale(currentSelection[0])
        });
      }
      return brushGroup;
    }

    /**
     * Creates brush handles DOM element
     * @param brushGroup
     */
    function addHandles(brushGroup) {
      brushGroup.selectAll('.resize path').remove();
      brushGroup.selectAll('.resize').append('path').attr('d', _.wrap(scales.yScale.range()[0], d3Helper.createDraggableBars));
    }
    var svgBackBrush = getSvgBrush();

    /**
     *
     * @param container - Element to which brush should be appended
     * @constructs Brush
     */
    var brush = function brush(container) {
      if (!domainRange) {
        throw new Error('Unspecified domainRange');
      }
      var brushScale = d3.scale.linear().domain(domainRange).range(scales.xScale.rangeExtent());
      svgBackBrush.x(brushScale);
      if (!backBrushGroup && !frontBrushGroup) {
        var brushHeight = scales.yScale.range()[0];
        backBrushGroup = createBrushGroup(container, 'back', brushScale, brushHeight);
        var handlesContainer = d3.select(container.node().parentNode).select('.post-data');
        frontBrushGroup = createBrushGroup(handlesContainer, 'front', brushScale, 0);
        addHandles(frontBrushGroup);
      }

      /**
       * Disable right click and allow context menu from chart.
       */
      backBrushGroup.selectAll('.extent').on('mousedown', function () {
        if (d3.event.button === 2) {
          d3.event.stopImmediatePropagation();
        }
      });
    };

    /**
     * Changes brush's opacity according to 'opaque' class
     */
    function toggleOpacity() {
      backBrushGroup.classed('opaque', !backBrushGroup.classed('opaque'));
    }

    /**
     * Check if left handler is visible
     * @param range
     * @returns {boolean}
     */
    function isLeftVisible(range) {
      var domain = svgBackBrush.x().domain();
      return range && range.length ? range[0] >= domain[0] : true;
    }

    /**
     * Check if right handler is visible
     * @param range
     * @returns {boolean}
     */
    function isRightVisible(range) {
      var domain = svgBackBrush.x().domain();
      return range && range.length ? range[1] <= domain[1] : true;
    }

    /**
     * Handler called on 'brushchange' event
     */
    function brushHandler() {
      var eventMode = d3.event.mode;
      var domain = svgBackBrush.x().domain();
      var domainLength = domain[1];

      // stop moving of a brush if one of its ends is not visible
      if (eventMode === 'move' && (!isLeftVisible(currentSelection) || !isRightVisible(currentSelection))) {
        brush.setSelection(currentSelection, false);
        return;
      }
      var roundedExtent = brushExtentService.roundExtent(svgBackBrush.extent(), eventMode);
      roundedExtent = brushExtentService.handleExtentEdgeCases(roundedExtent, domainLength);
      roundedExtent = brushExtentService.adjustBrushToPermittedSize(sizeRange, currentSelection, roundedExtent, domainLength);
      var skipDispatchingEvent = _.isEqual(currentSelection, roundedExtent);
      brush.setSelection(roundedExtent, skipDispatchingEvent);
    }

    /**
     * Handler called on 'brushchange' event
     */
    function brushStartHandler() {
      //stops the d3 event from passing to the parent container(carousel)
      d3.event.sourceEvent.stopPropagation();
      toggleOpacity();
      dispatch.brushstart(currentSelection);
    }

    /**
     * Handler called on 'brushend' event
     */
    function brushEndHandler() {
      toggleOpacity();
      dispatch.brushend(currentSelection);
    }

    /**
     * Checks if there's an initial selection
     * @returns {boolean}
     */
    brush.isSelectionInitialized = function () {
      return !_.isEqual(brush.getSelection(), [0, 0]);
    };

    /**
     * Updates brush (front and back) extent
     * @param {number[]} range
     */
    brush.updateBrush = function (range) {
      range = range || currentSelection;
      if (range && !_.isUndefined(backBrushGroup) && !_.isUndefined(frontBrushGroup)) {
        var extent = svgBackBrush.extent(range);
        backBrushGroup.call(extent);
        frontBrushGroup.call(extent);
        frontBrushGroup.selectAll('.resize.w').classed('hidden', !isLeftVisible(range));
        frontBrushGroup.selectAll('.resize.e').classed('hidden', !isRightVisible(range));
      }
    };

    /**
     * Returns current selection
     * @returns {number[]}
     */
    brush.getSelection = function () {
      return svgBackBrush.extent();
    };

    /**
     * Sets brush selection
     * @param {number[]} range
     * @param {boolean} [skipDispatchingEvent] - should 'brushchange' be fired
     */
    brush.setSelection = function (range, skipDispatchingEvent) {
      brush.updateBrush(range);
      if (!skipDispatchingEvent) {
        dispatch.brushchange(range, currentSelection, d3.event);
      }
      currentSelection = range;
    };

    /**
     * Sets domainRange to value
     * @param {boolean} value
     * @returns {brush}
     */
    brush.withDomainRange = function (value) {
      domainRange = value;
      return brush;
    };

    /**
     * Sets sizeRange to value
     * @param {boolean} value
     * @returns {brush}
     */
    brush.withSizeRange = function (value) {
      sizeRange = value;
      return brush;
    };

    /**
     * Sets brush's scales
     * @param {boolean} value
     * @returns {brush}
     */
    brush.scales = function (value) {
      scales = value;
      return brush;
    };

    /**
     * Clears the extent, making the brush extent empty
     */
    brush.clear = function () {
      svgBackBrush.clear();
    };
    brush.withParams = function (value) {
      params = value;
      return brush;
    };
    return d3.rebind(brush, dispatch, 'on');
  };
};
Brush.$inject = ['d3Helper', 'BrushExtentService'];
module.exports = Brush;